import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useMemo,
	useRef
} from "react";
import {
	ButtonClickEventArgs,
	ContextMenu,
	FontIconName,
	IconButton,
	useContextMenu
} from "client/Components";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { Coordinate } from "client/Misc/commonTypes";
import styles from "./ExpandedStackBar.module.less";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";

type MenuContext = {
	canStackBeDeletedByUser: boolean;
};

export type ExpandedStackBarProps = {
	stackId: string;
	title: string;
	canStackBeDeletedByUser: boolean;
	onCollapse(): void;
	onDelete(): void;
	onMute(): void;
};

export const ExpandedStackBar = (props: ExpandedStackBarProps): ReactElement => {
	const {
		stackId,
		title,
		canStackBeDeletedByUser,
		onCollapse,
		onDelete,
		onMute
	} = props;

	const moreButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const [contextMenuHandler, contextMenuProps] = useContextMenu(moreButtonRef);

	const actions = useMemo(
		(): ReadonlyArray<Action<MenuContext>> => [
			{
				kind: ActionKind.Regular,
				name: "Mute",
				title: localize("_Mute"),
				icon: "bell-slash",
				canExecute: () => true,
				execute: onMute
			},
			{
				kind: ActionKind.Regular,
				name: "Delete",
				title: localize("_Delete"),
				icon: "trash-can",
				canExecute: context => context.canStackBeDeletedByUser,
				execute: onDelete
			}
		],
		[onDelete, onMute]
	);

	const menuContext = useMemo(
		(): MenuContext => ({ canStackBeDeletedByUser }),
		[canStackBeDeletedByUser]
	);

	const trackingId: string = generateTrackingId("stack-bar", `|${stackId}`);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			<h4
				{...setTrackingId("title")}
				className={styles.title}
			>
				{title}
			</h4>

			<ExpandedStackBarButton
				onClick={onCollapse}
				trackingId="collapse"
				icon="chevron-up"
			/>

			<ExpandedStackBarButton
				ref={moreButtonRef}
				onClick={args => contextMenuHandler(args.at)}
				trackingId="more"
				icon="ellipsis"
				isActive={isNotNil(contextMenuProps)}
			/>

			{contextMenuProps && (
				<ContextMenu
					{...contextMenuProps}
					actions={actions}
					context={menuContext}
					trackingId={generateTrackingId(trackingId, "__context-menu")}
					title={title}
				/>
			)}
		</div>
	);
};

ExpandedStackBar.displayName = "ExpandedStackBar";

type ExpandedStackBarButtonClickEventArgs = {
	at: Coordinate;
};

type ExpandedStackBarButtonProps = {
	onClick(args: ExpandedStackBarButtonClickEventArgs): void;
	trackingId: string;
	icon: FontIconName;
	isActive?: boolean;
};

const ExpandedStackBarButton = forwardRef(
	(props: ExpandedStackBarButtonProps, ref: Ref<HTMLButtonElement>): ReactElement => {
		const {
			onClick,
			trackingId,
			icon,
			isActive
		} = props;

		const clickHandler = useCallback(
			(args: ButtonClickEventArgs): void => {
				const {
					clientX,
					clientY
				} = args;

				onClick({
					at: {
						x: clientX,
						y: clientY
					}
				});
			},
			[onClick]
		);

		return (
			<IconButton
				trackingId={trackingId}
				onClick={clickHandler}
				kind="transparent"
				icon={icon}
				ref={ref}
				bubbleClick={false}
				shape="round"
				isActive={isActive}
			/>
		);
	}
);

ExpandedStackBarButton.displayName = "ExpandedStackBarButton";